import { useEffect, useState } from "react";
import client from "@/lib/api";
import { SectionHeader, notifyError, notifyOk } from "./_ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function DecoderSettings() {
  const [vd, setVd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testVin, setTestVin] = useState("WAUZZZ4B23N055170");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    client.get("/admin/settings").then((r) => setVd(r.data.vin_decoder || {})).catch(notifyError);
  }, []);

  if (!vd) return <div className="text-sm text-neutral-500">Загрузка…</div>;

  const set = (k, v) => setVd({ ...vd, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/admin/settings", { vin_decoder: vd });
      notifyOk("Сохранено");
    } catch (e) { notifyError(e); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await client.put("/admin/settings", { vin_decoder: vd });
      const { data } = await client.post("/admin/decoder/test", { vin: testVin });
      setTestResult(data);
      if (data.ok) notifyOk("Подключение работает");
      else notifyError({ message: data.error || "Ошибка" });
    } catch (e) { notifyError(e); }
    finally { setTesting(false); }
  };

  const LANG_MODES = [
    ["auto", "Авто (язык сайта/акта)"],
    ["de", "Deutsch"], ["en", "English"], ["pl", "Polski"], ["ru", "Русский"],
  ];

  return (
    <div className="max-w-2xl">
      <SectionHeader
        title="Настройки VIN-декодера"
        description="Своя база используется всегда. Внешний провайдер — опционально, по умолчанию выключен."
        action={
          <Button data-testid="decoder-save-btn" onClick={save} disabled={saving} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            {saving ? "Сохранение…" : "Сохранить"}
          </Button>
        }
      />

      <div className="border border-neutral-200 rounded-lg bg-white p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Использовать внешний источник</Label>
            <p className="text-xs text-neutral-500 mt-0.5">Платный провайдер, включается при наличии токена.</p>
          </div>
          <Switch data-testid="decoder-external-switch" checked={!!vd.use_external} onCheckedChange={(v) => set("use_external", v)} />
        </div>

        <div className={vd.use_external ? "space-y-4" : "space-y-4 opacity-50 pointer-events-none"}>
          <div className="space-y-1.5">
            <Label>Провайдер</Label>
            <Select value={vd.provider || "partsapi"} onValueChange={(v) => set("provider", v)}>
              <SelectTrigger data-testid="decoder-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="partsapi">partsapi.ru</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>API URL</Label>
            <Input data-testid="decoder-url" value={vd.api_url || ""} onChange={(e) => set("api_url", e.target.value)} className="border-neutral-300" placeholder="https://api.partsapi.ru/..." />
          </div>
          <div className="space-y-1.5">
            <Label>API токен</Label>
            <Input data-testid="decoder-token" value={vd.api_token || ""} onChange={(e) => set("api_token", e.target.value)} className="border-neutral-300" type="password" />
          </div>
          <div className="space-y-1.5">
            <Label>Язык ответа</Label>
            <Select value={vd.lang_mode || "auto"} onValueChange={(v) => set("lang_mode", v)}>
              <SelectTrigger data-testid="decoder-langmode-select" className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANG_MODES.map(([v, label]) => (
                  <SelectItem key={v} value={v}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500">«Авто» — язык ответа партнёра подстраивается под язык публичного сайта / акта.</p>
          </div>

          <div className="border-t border-neutral-100 pt-4 space-y-2">
            <Label>Проверить подключение</Label>
            <div className="flex gap-2">
              <Input data-testid="decoder-test-vin" value={testVin} onChange={(e) => setTestVin(e.target.value.toUpperCase())} className="border-neutral-300 font-mono flex-1" placeholder="VIN для теста" />
              <Button data-testid="decoder-test-btn" onClick={runTest} disabled={testing} variant="outline">
                {testing ? "Проверка…" : "Проверить"}
              </Button>
            </div>
            {testResult && (
              <div data-testid="decoder-test-result" className={`text-xs rounded-md p-3 mt-2 ${testResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>
                {testResult.ok ? (
                  <div>
                    <div className="font-medium mb-1">Success — {testResult.result?.brand} {testResult.result?.model}</div>
                    <div className="text-neutral-600">{[testResult.result?.modification, testResult.result?.fuel_type, testResult.result?.year_from].filter(Boolean).join(" · ")}</div>
                  </div>
                ) : (testResult.error || "Ошибка")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
